// Generates a 2-page AXIS Sample Report PDF using jsPDF + RoleIQ branding (Navy #0F1E35, Gold #C9A84C)
import jsPDF from "jspdf";

const NAVY = "#0F1E35";
const GOLD = "#C9A84C";
const CREAM = "#F8F6F2";

export function generateAxisSampleReport() {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 48;
  const maxWidth = pageWidth - margin * 2;

  // ─── PAGE 1 ───
  // Header band
  doc.setFillColor(15, 30, 53); // navy
  doc.rect(0, 0, pageWidth, 90, "F");
  doc.setFillColor(201, 168, 76); // gold accent
  doc.rect(0, 86, pageWidth, 4, "F");

  // Brand
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(255, 255, 255);
  doc.text("Role", margin, 50);
  doc.setTextColor(201, 168, 76);
  doc.text("IQ", margin + 40, 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text("AI-Powered Intelligence Platform", margin, 68);
  doc.setFontSize(9);
  doc.setTextColor(201, 168, 76);
  doc.text("AXIS Assessment — Sample Report", pageWidth - margin, 50, { align: "right" });
  doc.setTextColor(255, 255, 255);
  doc.text("Confidential · For Demonstration Only", pageWidth - margin, 68, { align: "right" });

  let y = 130;

  // Personal info card
  doc.setDrawColor(201, 168, 76);
  doc.setLineWidth(0.6);
  doc.setFillColor(248, 246, 242);
  doc.roundedRect(margin, y, maxWidth, 80, 6, 6, "FD");
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(15, 30, 53);
  doc.text("Priya Sharma", margin + 18, y + 28);
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 80);
  doc.text("HR Manager · Mid-Career Professional", margin + 18, y + 46);
  doc.text("Assessment Date: 12 February 2026  ·  Tokens Used: 50  ·  Validity: 24 months", margin + 18, y + 64);

  y += 105;

  // Section title — Profile Snapshot
  doc.setFillColor(15, 30, 53);
  doc.rect(margin, y, maxWidth, 26, "F");
  doc.setTextColor(201, 168, 76);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("YOUR AXIS PROFILE", margin + 12, y + 18);
  y += 42;

  // Score bars — A:High, X:Medium, I:High, S:Low
  const dims = [
    { letter: "A", name: "Achievement", level: "HIGH", score: 0.85, line: "How you pursue goals and drive results" },
    { letter: "X", name: "eXpression", level: "MEDIUM", score: 0.55, line: "How you communicate and share ideas" },
    { letter: "I", name: "Influence", level: "HIGH", score: 0.82, line: "How you build relationships and lead others" },
    { letter: "S", name: "Stability", level: "LOW", score: 0.32, line: "How you manage change and maintain balance" },
  ];

  dims.forEach((d) => {
    // Letter circle
    doc.setFillColor(201, 168, 76);
    doc.circle(margin + 16, y + 14, 14, "F");
    doc.setTextColor(15, 30, 53);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(d.letter, margin + 16, y + 19, { align: "center" });

    // Name + line
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(15, 30, 53);
    doc.text(d.name, margin + 42, y + 12);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 120, 120);
    doc.text(d.line, margin + 42, y + 24);

    // Level badge
    const badgeColor = d.level === "HIGH" ? [201, 168, 76] : d.level === "MEDIUM" ? [120, 120, 120] : [180, 100, 100];
    doc.setFillColor(badgeColor[0], badgeColor[1], badgeColor[2]);
    doc.roundedRect(pageWidth - margin - 70, y + 4, 70, 20, 10, 10, "F");
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(d.level, pageWidth - margin - 35, y + 17, { align: "center" });

    // Bar
    const barX = margin + 42;
    const barY = y + 32;
    const barW = pageWidth - margin - barX - 80;
    doc.setFillColor(232, 232, 232);
    doc.roundedRect(barX, barY, barW, 6, 3, 3, "F");
    doc.setFillColor(badgeColor[0], badgeColor[1], badgeColor[2]);
    doc.roundedRect(barX, barY, barW * d.score, 6, 3, 3, "F");

    y += 56;
  });

  y += 8;

  // What this means
  doc.setFillColor(15, 30, 53);
  doc.rect(margin, y, maxWidth, 26, "F");
  doc.setTextColor(201, 168, 76);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("WHAT YOUR PROFILE MEANS", margin + 12, y + 18);
  y += 42;

  doc.setTextColor(40, 40, 40);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const meaning =
    "Priya, your profile suggests you are a results-driven HR Manager who blends ambition (High A) with strong relational influence (High I). You communicate effectively when needed (Medium X) but prefer purposeful conversation over broad self-promotion. Your Low Stability score indicates you thrive in dynamic, fast-changing environments — and may find slow-moving processes draining. This combination is particularly powerful in transformation-heavy HR roles such as M&A integration, culture change, or rapid scaling.";
  const meaningLines = doc.splitTextToSize(meaning, maxWidth);
  doc.text(meaningLines, margin, y);
  y += meaningLines.length * 14;

  // Footer page 1
  doc.setDrawColor(201, 168, 76);
  doc.setLineWidth(0.5);
  doc.line(margin, pageHeight - 40, pageWidth - margin, pageHeight - 40);
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text("RoleIQ AXIS Sample Report  ·  Page 1 of 2  ·  roleiq.pro", margin, pageHeight - 24);

  // ─── PAGE 2 ───
  doc.addPage();
  doc.setFillColor(15, 30, 53);
  doc.rect(0, 0, pageWidth, 60, "F");
  doc.setFillColor(201, 168, 76);
  doc.rect(0, 56, pageWidth, 4, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.text("Role", margin, 36);
  doc.setTextColor(201, 168, 76);
  doc.text("IQ", margin + 24, 36);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("AXIS Sample Report — Priya Sharma", pageWidth - margin, 36, { align: "right" });

  y = 90;

  // Strengths
  doc.setFillColor(15, 30, 53);
  doc.rect(margin, y, maxWidth, 26, "F");
  doc.setTextColor(201, 168, 76);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("YOUR STRENGTHS", margin + 12, y + 18);
  y += 38;

  const strengths = [
    "Outcomes-First Leadership — Your High A combined with High I lets you set ambitious targets and rally teams behind them. You measure progress relentlessly.",
    "Stakeholder Mastery — Your High I means you intuitively read political climates, build cross-functional bridges, and unblock resources where others get stuck.",
    "Bias for Action — Your Low S keeps you energised in ambiguity. You make calls when others wait for certainty — a critical edge in scaling and transformation work.",
    "Calibrated Voice — Your Medium X means you speak with intention. Stakeholders trust your input because you don't dilute it with noise.",
  ];
  doc.setTextColor(40, 40, 40);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  strengths.forEach((s) => {
    doc.setTextColor(201, 168, 76);
    doc.setFont("helvetica", "bold");
    doc.text("•", margin, y);
    doc.setTextColor(40, 40, 40);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(s, maxWidth - 14);
    doc.text(lines, margin + 14, y);
    y += lines.length * 13 + 4;
  });

  y += 6;

  // Growth Edges
  doc.setFillColor(15, 30, 53);
  doc.rect(margin, y, maxWidth, 26, "F");
  doc.setTextColor(201, 168, 76);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("YOUR GROWTH EDGES", margin + 12, y + 18);
  y += 38;

  const edges = [
    "Process Patience (Low S) — You may experience frustration with structured rollouts, compliance-heavy initiatives, or stakeholders who need long deliberation. Risk: appearing impatient or dismissive of slower colleagues.",
    "Visibility Gap (Medium X) — Your work is excellent but your storytelling about it is understated. In senior conversations, this can mean your contributions get under-credited.",
    "Achievement Tunnel (High A) — Under pressure, you may push through resistance rather than pause to address root cause. This can leave team morale fragile after big wins.",
  ];
  doc.setTextColor(40, 40, 40);
  doc.setFontSize(10);
  edges.forEach((s) => {
    doc.setTextColor(180, 100, 100);
    doc.setFont("helvetica", "bold");
    doc.text("•", margin, y);
    doc.setTextColor(40, 40, 40);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(s, maxWidth - 14);
    doc.text(lines, margin + 14, y);
    y += lines.length * 13 + 4;
  });

  y += 6;

  // Recommendations
  doc.setFillColor(15, 30, 53);
  doc.rect(margin, y, maxWidth, 26, "F");
  doc.setTextColor(201, 168, 76);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("3 DEVELOPMENT RECOMMENDATIONS", margin + 12, y + 18);
  y += 38;

  const recs = [
    {
      title: "1. Build a Stability Anchor Practice",
      body: "Adopt one weekly 30-minute structured planning ritual. Use it to convert your bursts of action into documented playbooks others can follow. This compounds your impact and reduces the firefighting load on your team.",
    },
    {
      title: "2. Schedule Visibility Conversations",
      body: "Once per month, book 25 minutes with your CHRO or business head to share three concrete wins and one ask. Your Medium X profile means visibility won't happen organically — you must architect it.",
    },
    {
      title: "3. Invest in a Slow-Burn Stakeholder",
      body: "Identify one cross-functional leader who operates with High S (process-oriented, deliberate). Build a peer-coaching relationship with them. They will sharpen your governance instincts; you will accelerate their action bias.",
    },
  ];
  doc.setFontSize(10);
  recs.forEach((r) => {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(15, 30, 53);
    doc.text(r.title, margin, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    const lines = doc.splitTextToSize(r.body, maxWidth);
    doc.text(lines, margin, y);
    y += lines.length * 13 + 8;
  });

  // Footer page 2
  doc.setDrawColor(201, 168, 76);
  doc.setLineWidth(0.5);
  doc.line(margin, pageHeight - 60, pageWidth - margin, pageHeight - 60);
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  const disclaimer = "Note: AI-generated content may contain inaccuracies. Please review all competencies with a qualified HR professional before formal use in hiring, performance management, or development processes. RoleIQ is a decision-support tool — not a substitute for professional HR judgement.";
  const dlines = doc.splitTextToSize(disclaimer, maxWidth);
  doc.text(dlines, margin, pageHeight - 50);
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text("RoleIQ AXIS Sample Report  ·  Page 2 of 2  ·  roleiq.pro  ·  hello@roleiq.pro", margin, pageHeight - 18);

  doc.save("RoleIQ_AXIS_Sample_Report_Priya_Sharma.pdf");
}
